import * as preact from "preact";

import * as keyboardShortcuts from "keyboardShortcuts";
import * as practiceUtils from "practiceUtils";
import { ChallengeProps } from "practiceUtils";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import ExplanationView from "views/ExplanationView";
import PracticeContextView from "views/PracticeContextView";
import PracticePlayTTSView from "views/PracticePlayTTSView";
import TransliterationView from "./TransliterationView";

const cardModes = [
  {
    type: "active",
    displayString: "Active (includes typing)",
  },
  {
    type: "passive",
    displayString: "Passive (no typing)",
  },
  {
    type: "delete",
    displayString: "Delete this {{wordOrPhraseString}}",
  },
] as const;

class IntroChallengeView extends preact.Component<ChallengeProps> {
  userWords: UserWordStore[];

  constructor(props: ChallengeProps) {
    super(props);

    setTimeout(() => {
      this.props.completedCallback({
        type: "allow-continue",
      });
    }, 1000);
  }

  render() {
    const { userWord } = this.props;
    // Computed during render (not cached in state) so it reflects edits to the
    // word's context made mid-session, e.g. via the Edit Card modal.
    const clozeSentence = practiceUtils.getClozeSentence(
      userWord,
      this.props.selectedContextIndex
    );

    const alternativeTranslations =
      userWord.state.value.alternativeTranslations;

    const wordOrPhraseString = userWord.isPhrase() ? "phrase" : "word";

    return (
      <div class="introChallenge">
        <h3 class="newWord">NEW {wordOrPhraseString.toUpperCase()}</h3>
        <h2
          {...(languageMap.map[userWord.state.value.language]?.direction ===
          "RTL"
            ? { dir: "rtl" }
            : {})}
        >
          <PracticePlayTTSView
            text={userWord.state.value.word}
            language={userWord.state.value.language}
          />{" "}
          <span class="transliterationContainer">
            <TransliterationView
              text={userWord.state.value.word}
              language={userWord.state.value.language}
            />
            <div>{userWord.state.value.word}</div>
          </span>
        </h2>

        <p>
          <PracticeContextView
            clozeSentence={clozeSentence}
            rtl={
              languageMap.map[userWord.state.value.language]?.direction ===
              "RTL"
            }
            mode={{ type: "complete" }}
          />
        </p>

        {practiceUtils.showContextSource ? (
          <p>
            <small>
              From:{" "}
              {
                userWord.state.value.contexts[this.props.selectedContextIndex]
                  ?.bookID?.title
              }
            </small>
          </p>
        ) : null}

        <p>
          Meaning: <strong>{userWord.state.value.translation}</strong>
          {alternativeTranslations.length > 0
            ? ` / ${userWord.state.value.alternativeTranslations.join(" / ")}`
            : null}
        </p>

        <ExplanationView
          word={userWord.state.value.word}
          context={clozeSentence.map((part) => part.text).join("")}
          language={userWord.state.value.language}
          showInFirstLanguage={false}
          cacheOnly={true}
          existingTranslations={[
            userWord.state.value.translation,
            ...userWord.state.value.alternativeTranslations,
          ]}
          onExplanationFetched={() => this.props.showingExplanation()}
        />
        <ExplanationView
          word={userWord.state.value.word}
          context={clozeSentence.map((part) => part.text).join("")}
          language={userWord.state.value.language}
          showInFirstLanguage={true}
          cacheOnly={true}
          existingTranslations={[
            userWord.state.value.translation,
            ...userWord.state.value.alternativeTranslations,
          ]}
          onExplanationFetched={() => this.props.showingExplanation()}
        />

        {stores.user.state.value.practiceType === "new" ? (
          <div class="newWordOptions">
            <p>
              <strong>Learning mode for this word</strong>
            </p>
            <ul>
              {cardModes.map((mode, modeIndex) => (
                <li>
                  <label>
                    <input
                      type="radio"
                      name="learn-mode"
                      checked={this.props.introCardMode === mode.type}
                      onClick={() => {
                        this.props.setCardMode(mode.type);
                      }}
                    ></input>{" "}
                    <small>[{modeIndex + 1}]</small>{" "}
                    {mode.displayString.replace(
                      "{{wordOrPhraseString}}",
                      wordOrPhraseString
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  componentDidMount() {
    for (const [index, mode] of cardModes.entries()) {
      keyboardShortcuts.registerHandler(
        "practicePage",
        `${index + 1}`.charCodeAt(0),
        () => {
          this.props.setCardMode(mode.type);
        }
      );
    }
  }

  componentWillUnmount(): void {
    for (const [index] of cardModes.entries()) {
      keyboardShortcuts.unregisterHandler(
        "practicePage",
        `${index + 1}`.charCodeAt(0)
      );
    }
  }
}

export default IntroChallengeView;
